import type { Complaint, MediaWithUrl } from '../api';
import NetworkManager from '../api/NetworkManager';

/**
 * Download a media file by its S3 key via the backend proxy (avoids CORS).
 * Falls back to direct URL fetch if proxy fails.
 * Returns base64 data URL or null.
 */
async function imageKeyToBase64(mediaKey: string, directUrl?: string): Promise<string | null> {
  // Primary: fetch via our own API proxy (same origin, no CORS issues)
  try {
    const proxyUrl = NetworkManager.getBaseUrl() + `/media/${encodeURIComponent(mediaKey)}`;
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const env = localStorage.getItem('mapp_environment');
    if (env) headers['x-mapp-environment'] = env;
    const brand = localStorage.getItem('mapp_brand_id');
    if (brand) headers['x-brand-id'] = brand;

    const response = await fetch(proxyUrl, { headers });
    if (response.ok) {
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    }
  } catch {
    // Proxy failed, try direct URL
  }

  // Fallback: direct presigned URL
  if (directUrl) {
    try {
      const response = await fetch(directUrl);
      if (response.ok) {
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      }
    } catch {
      // Both methods failed
    }
  }

  return null;
}

/**
 * Render the Byte logo SVG to a base64 PNG using a canvas.
 */
async function renderLogoToBase64(): Promise<string | null> {
  const svgString = `<svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0)"><g clip-path="url(#clip1)">
<path d="M14.4827 28.9811H10.6571V24.9686H6.82557V20.956H3V3H14.4827C17.5527 3 19.9272 3.66038 21.6062 4.98113C23.2911 6.30189 24.1306 8.16981 24.1306 10.5849C24.1306 12.195 23.7408 13.5723 22.9673 14.7233C22.1938 15.8742 21.0785 16.717 19.6214 17.2579C21.3423 17.7358 22.6675 18.6164 23.6029 19.9057C24.5323 21.195 25 22.7925 25 24.6981C25 26.4214 24.5983 27.9057 23.8008 29.1572C23.0033 30.4025 21.846 31.3585 20.335 32.0126C18.8239 32.6667 17.0191 33 14.9204 33H14.4767L14.4827 28.9811ZM3 28.9811H6.82557V33H3V28.9811Z" fill="url(#paint0)"/>
</g></g>
<defs>
<linearGradient id="paint0" x1="-0.390404" y1="30.6388" x2="23.4851" y2="7.87606" gradientUnits="userSpaceOnUse">
<stop offset="0.28" stop-color="#FAA51F"/><stop offset="0.67" stop-color="#DE1D3B"/><stop offset="1" stop-color="#662C8B"/>
</linearGradient>
<clipPath id="clip0"><rect width="28" height="36" fill="white"/></clipPath>
<clipPath id="clip1"><rect width="22" height="30" fill="white" transform="translate(3 3)"/></clipPath>
</defs></svg>`;

  return new Promise((resolve) => {
    const scale = 3; // high-DPI
    const img = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 28 * scale;
      canvas.height = 36 * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, 28, 36);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

function formatDate(timestamp?: number): string {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_COLORS: Record<string, [number, number, number]> = {
  submitted: [59, 130, 246],   // blue
  under_review: [245, 158, 11], // amber
  approved: [16, 185, 129],     // green
  rejected: [239, 68, 68],      // red
};

// Brand colors from the Byte gradient
const BRAND_PRIMARY: [number, number, number] = [222, 29, 59]; // #DE1D3B
const BRAND_DARK: [number, number, number] = [31, 41, 55];     // #1f2937

// Embedded Unicode font. jsPDF's built-in Helvetica only supports WinAnsi
// (Latin-1), so Vietnamese/Cyrillic/Greek render as garbled, spaced-out glyphs
// (HBAC2-8838). Noto Sans (subset) covers Latin + Latin Extended (Vietnamese),
// Cyrillic, and Greek. Registered on the doc via `registerFonts`.
const PDF_FONT = 'NotoSans';

/**
 * Register the embedded Noto Sans font (regular + bold) on a jsPDF document so
 * non-Latin-1 characters render correctly. Fonts are dynamically imported so
 * the ~260 KB of base64 is code-split into the PDF chunk, not the main bundle.
 */
async function registerFonts(doc: import('jspdf').jsPDF): Promise<void> {
  const [{ NOTO_SANS_REGULAR_BASE64 }, { NOTO_SANS_BOLD_BASE64 }] = await Promise.all([
    import('../assets/fonts/notoSansRegular'),
    import('../assets/fonts/notoSansBold'),
  ]);
  doc.addFileToVFS('NotoSans-Regular.ttf', NOTO_SANS_REGULAR_BASE64);
  doc.addFont('NotoSans-Regular.ttf', PDF_FONT, 'normal');
  doc.addFileToVFS('NotoSans-Bold.ttf', NOTO_SANS_BOLD_BASE64);
  doc.addFont('NotoSans-Bold.ttf', PDF_FONT, 'bold');
  doc.setFont(PDF_FONT, 'normal');
}

/** NFC-normalize so precomposed Vietnamese glyphs map to single font glyphs. */
function nz(value: unknown): string {
  return (value == null ? '' : String(value)).normalize('NFC');
}

export interface GeneratedPdf {
  blob: Blob;
  filename: string;
}

/**
 * Build a PDF report for a complaint and return it as a Blob.
 *
 * Delivery is the caller's responsibility (download vs share sheet vs inline
 * viewer) so that platform-specific quirks — primarily Mobile Safari / WKWebView
 * ignoring `<a download>` — can be handled outside this function. See
 * `utils/deliverPdf.ts`.
 */
export async function generateComplaintPdf(
  complaint: Complaint & { media?: MediaWithUrl[] },
  categoryLabel?: string
): Promise<GeneratedPdf> {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF('p', 'mm', 'a4');
  await registerFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;

  // ── Header band ──────────────────────────────────────────────────
  const headerHeight = 28;
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Logo
  const logoBase64 = await renderLogoToBase64();
  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', margin, 5, 7, 9);
  }

  // App name
  doc.setFontSize(14);
  doc.setFont(PDF_FONT, 'bold');
  doc.setTextColor(255, 255, 255);
  const nameX = logoBase64 ? margin + 10 : margin;
  doc.text('Byte Quality', nameX, 12.5);
  doc.setFontSize(8);
  doc.setFont(PDF_FONT, 'normal');
  doc.setTextColor(180, 190, 210);
  doc.text('Quality Complaints', nameX, 17);

  // Reference badge (right side of header)
  const refText = complaint.referenceNumber || '';
  doc.setFontSize(9);
  doc.setFont(PDF_FONT, 'bold');
  doc.setTextColor(255, 255, 255);
  const refWidth = doc.getTextWidth(refText) + 8;
  const refX = pageWidth - margin - refWidth;
  doc.setFillColor(...BRAND_PRIMARY);
  doc.roundedRect(refX, 7, refWidth, 7, 1.5, 1.5, 'F');
  doc.text(refText, refX + 4, 12);

  y = headerHeight + 8;

  // ── Title line ───────────────────────────────────────────────────
  doc.setTextColor(...BRAND_DARK);
  doc.setFontSize(16);
  doc.setFont(PDF_FONT, 'bold');
  doc.text('Quality Complaint Report', margin, y);
  y += 6;

  doc.setFontSize(8);
  doc.setFont(PDF_FONT, 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Generated on ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`,
    margin,
    y
  );

  // Status badge next to generated date
  const statusLabel = STATUS_LABELS[complaint.status] || complaint.status;
  const statusColor = STATUS_COLORS[complaint.status] || BRAND_DARK;
  const statusX = margin + 60;
  doc.setFillColor(...statusColor);
  const statusTextWidth = doc.getTextWidth(statusLabel) + 6;
  doc.roundedRect(statusX, y - 3, statusTextWidth, 4.5, 1, 1, 'F');
  doc.setFontSize(7);
  doc.setFont(PDF_FONT, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(statusLabel, statusX + 3, y);
  y += 5;

  // Thin accent line
  doc.setDrawColor(...BRAND_PRIMARY);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + 30, y);
  doc.setLineWidth(0.2);
  y += 6;

  // ── Details table ────────────────────────────────────────────────
  const detailRows: string[][] = [
    ['Restaurant', nz(`${complaint.storeName} (${complaint.champsNumber || complaint.storeId})`)],
    ['Distributor', nz(complaint.supplierName)],
  ];
  if (complaint.manufacturer) detailRows.push(['Manufacturer', nz(complaint.manufacturer)]);
  detailRows.push(['Category', nz(categoryLabel || complaint.categoryLabel || complaint.category)]);
  detailRows.push([
    'Product',
    nz(`${complaint.productName}${complaint.productCode ? ` (${complaint.productCode})` : ''}`),
  ]);
  if (complaint.deliveryDate) detailRows.push(['Delivery Date', formatDate(complaint.deliveryDate)]);
  if (complaint.batchCode) detailRows.push(['Batch Code', nz(complaint.batchCode)]);
  if (complaint.affectedUnits) {
    const unitLabel = complaint.affectedUnitType === 'BAG' ? 'Bag(s)' : complaint.affectedUnitType === 'CASE' ? 'Case(s)' : '';
    detailRows.push(['Affected Units', unitLabel ? `${complaint.affectedUnits} ${unitLabel}` : String(complaint.affectedUnits)]);
  }
  if (complaint.claimValue != null) {
    detailRows.push(['Value of Claim', `£${Number(complaint.claimValue).toFixed(2)}`]);
  }
  autoTable(doc, {
    startY: y,
    body: detailRows,
    theme: 'plain',
    styles: { font: PDF_FONT, fontSize: 9, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 } },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 42, textColor: [80, 80, 80] },
      1: { textColor: [30, 30, 30] },
    },
    alternateRowStyles: { fillColor: [248, 249, 250] },
    margin: { left: margin, right: margin },
    didDrawCell: (data) => {
      // Draw subtle bottom border on each row
      if (data.row.index < detailRows.length - 1 && data.column.index === 1) {
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.15);
        doc.line(
          margin,
          data.cell.y + data.cell.height,
          pageWidth - margin,
          data.cell.y + data.cell.height
        );
      }
    },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // ── Description ──────────────────────────────────────────────────
  doc.setFontSize(11);
  doc.setFont(PDF_FONT, 'bold');
  doc.setTextColor(...BRAND_DARK);
  doc.text('Description', margin, y);
  y += 1;
  // Small accent underline
  doc.setDrawColor(...BRAND_PRIMARY);
  doc.setLineWidth(0.4);
  doc.line(margin, y, margin + 18, y);
  doc.setLineWidth(0.2);
  y += 4;

  doc.setFontSize(9);
  doc.setFont(PDF_FONT, 'normal');
  doc.setTextColor(50, 50, 50);
  const descLines = doc.splitTextToSize(nz(complaint.description), contentWidth);
  if (y + descLines.length * 4 > pageHeight - 20) {
    doc.addPage();
    y = margin;
  }
  doc.text(descLines, margin, y);
  y += descLines.length * 4 + 8;

  // ── Evidence Photos ──────────────────────────────────────────────
  const imagesToEmbed: { label: string; key?: string; url?: string }[] = [];

  if (complaint.bagPictureUrl) {
    const bagMedia = complaint.media?.find((m) => m.key === complaint.bagPictureUrl);
    imagesToEmbed.push({ label: 'Bag Picture', key: complaint.bagPictureUrl, url: bagMedia?.downloadUrl });
  }
  if (complaint.casePictureUrl) {
    const caseMedia = complaint.media?.find((m) => m.key === complaint.casePictureUrl);
    imagesToEmbed.push({ label: 'Case Picture', key: complaint.casePictureUrl, url: caseMedia?.downloadUrl });
  }

  // Also include additional attachments
  const additionalMedia = complaint.media?.filter(
    (m) => m.key !== complaint.bagPictureUrl && m.key !== complaint.casePictureUrl
  ) || [];

  if (imagesToEmbed.length > 0 || additionalMedia.length > 0) {
    if (y + 80 > pageHeight - 20) {
      doc.addPage();
      y = margin;
    }

    doc.setFontSize(11);
    doc.setFont(PDF_FONT, 'bold');
    doc.setTextColor(...BRAND_DARK);
    doc.text('Evidence Photos', margin, y);
    y += 1;
    doc.setDrawColor(...BRAND_PRIMARY);
    doc.setLineWidth(0.4);
    doc.line(margin, y, margin + 22, y);
    doc.setLineWidth(0.2);
    y += 5;

    if (imagesToEmbed.length > 0) {
      const imageWidth = imagesToEmbed.length > 1 ? (contentWidth - 6) / 2 : contentWidth / 2;
      const imageHeight = 55;

      // Pre-fetch all images in parallel (via backend proxy to avoid CORS)
      const imageResults = await Promise.all(
        imagesToEmbed.map(async (img) => ({
          ...img,
          base64: img.key ? await imageKeyToBase64(img.key, img.url) : null,
        }))
      );

      for (let i = 0; i < imageResults.length; i++) {
        const img = imageResults[i];
        const xOffset = margin + i * (imageWidth + 6);

        // Label above image
        doc.setFontSize(7);
        doc.setFont(PDF_FONT, 'bold');
        doc.setTextColor(100, 100, 100);
        doc.text(img.label.toUpperCase(), xOffset, y);

        const imgY = y + 2;

        if (img.base64) {
          try {
            // Draw rounded border
            doc.setDrawColor(220, 220, 220);
            doc.roundedRect(xOffset, imgY, imageWidth, imageHeight, 2, 2, 'S');
            doc.addImage(img.base64, 'JPEG', xOffset + 0.5, imgY + 0.5, imageWidth - 1, imageHeight - 1);
          } catch {
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(xOffset, imgY, imageWidth, imageHeight, 2, 2, 'FD');
            doc.setFontSize(8);
            doc.setFont(PDF_FONT, 'normal');
            doc.setTextColor(160, 160, 160);
            doc.text('[Image could not be embedded]', xOffset + imageWidth / 2, imgY + imageHeight / 2, {
              align: 'center',
            });
          }
        } else {
          doc.setFillColor(248, 249, 250);
          doc.setDrawColor(220, 220, 220);
          doc.roundedRect(xOffset, imgY, imageWidth, imageHeight, 2, 2, 'FD');
          doc.setFontSize(8);
          doc.setFont(PDF_FONT, 'normal');
          doc.setTextColor(160, 160, 160);
          doc.text('[Image unavailable]', xOffset + imageWidth / 2, imgY + imageHeight / 2, {
            align: 'center',
          });
        }
      }

      y += imageHeight + 8;
    }

    // ── Additional Evidence (E14.2) ────────────────────────────────
    // Split additional media into images (embed inline, capped) and other
    // files (render as clickable links).
    if (additionalMedia.length > 0) {
      const MAX_ADDITIONAL_INLINE_IMAGES = 6;
      const additionalImages = additionalMedia.filter((m) => {
        const mime = m.mimeType || '';
        if (mime.startsWith('image/')) return true;
        if (mime) return false;
        // Fallback for legacy responses without mimeType: check extension
        return /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(m.key);
      });
      const additionalNonImages = additionalMedia.filter((m) => !additionalImages.includes(m));

      const inlineImages = additionalImages.slice(0, MAX_ADDITIONAL_INLINE_IMAGES);
      const overflowImages = additionalImages.slice(MAX_ADDITIONAL_INLINE_IMAGES);
      const additionalImageHeight = 50;

      // Keep heading with first content row to avoid orphan headings.
      // First row is either an image row (~55mm) or the first link line (~5mm).
      const firstRowHeight = inlineImages.length > 0
        ? additionalImageHeight + 6
        : (overflowImages.length + additionalNonImages.length > 0 ? 8 : 0);
      const headingHeight = 9;
      if (y + headingHeight + firstRowHeight > pageHeight - 18) {
        doc.addPage();
        y = margin;
      }

      // Section heading
      doc.setFontSize(11);
      doc.setFont(PDF_FONT, 'bold');
      doc.setTextColor(...BRAND_DARK);
      doc.text('Additional Evidence', margin, y);
      y += 1;
      doc.setDrawColor(...BRAND_PRIMARY);
      doc.setLineWidth(0.4);
      doc.line(margin, y, margin + 26, y);
      doc.setLineWidth(0.2);
      y += 5;

      if (inlineImages.length > 0) {
        const imgResults = await Promise.all(
          inlineImages.map(async (m) => ({
            media: m,
            base64: await imageKeyToBase64(m.key, m.downloadUrl),
          }))
        );

        const imageWidth = (contentWidth - 6) / 2;
        const imageHeight = additionalImageHeight;

        for (let i = 0; i < imgResults.length; i += 2) {
          // Page break before each row if it doesn't fit on the current page
          if (y + imageHeight + 6 > pageHeight - 18) {
            doc.addPage();
            y = margin;
          }
          for (let j = 0; j < 2 && i + j < imgResults.length; j++) {
            const item = imgResults[i + j];
            const xOffset = margin + j * (imageWidth + 6);

            // File-name label above the image (truncate if too wide)
            const label = nz(item.media.fileName || item.media.key.split('/').pop() || 'attachment');
            doc.setFontSize(7);
            doc.setFont(PDF_FONT, 'bold');
            doc.setTextColor(100, 100, 100);
            const truncated = label.length > 36 ? label.slice(0, 33) + '…' : label;
            doc.text(truncated, xOffset, y);

            const imgY = y + 2;
            if (item.base64) {
              try {
                doc.setDrawColor(220, 220, 220);
                doc.roundedRect(xOffset, imgY, imageWidth, imageHeight, 2, 2, 'S');
                doc.addImage(item.base64, 'JPEG', xOffset + 0.5, imgY + 0.5, imageWidth - 1, imageHeight - 1);
              } catch {
                doc.setFillColor(248, 249, 250);
                doc.roundedRect(xOffset, imgY, imageWidth, imageHeight, 2, 2, 'FD');
              }
            } else {
              doc.setFillColor(248, 249, 250);
              doc.setDrawColor(220, 220, 220);
              doc.roundedRect(xOffset, imgY, imageWidth, imageHeight, 2, 2, 'FD');
              doc.setFontSize(8);
              doc.setFont(PDF_FONT, 'normal');
              doc.setTextColor(160, 160, 160);
              doc.text('[Image unavailable]', xOffset + imageWidth / 2, imgY + imageHeight / 2, {
                align: 'center',
              });
            }
          }
          y += imageHeight + 6;
        }
      }

      // Render non-image files + overflow images as clickable links.
      const linkItems = [...overflowImages, ...additionalNonImages];
      if (linkItems.length > 0) {
        if (y + 8 > pageHeight - 20) {
          doc.addPage();
          y = margin;
        }
        doc.setFontSize(8);
        doc.setFont(PDF_FONT, 'bold');
        doc.setTextColor(100, 100, 100);
        doc.text('Attachments', margin, y);
        y += 4;

        for (const m of linkItems) {
          if (y + 5 > pageHeight - 20) {
            doc.addPage();
            y = margin;
          }
          const fileName = nz(m.fileName || m.key.split('/').pop() || 'attachment');
          const mime = m.mimeType || '';
          const badge = mime.startsWith('image/')
            ? '[IMG]'
            : mime === 'application/pdf'
              ? '[PDF]'
              : mime.startsWith('video/')
                ? '[VIDEO]'
                : '[FILE]';

          doc.setFontSize(8);
          doc.setFont(PDF_FONT, 'bold');
          doc.setTextColor(120, 120, 120);
          doc.text(badge, margin, y);

          doc.setFont(PDF_FONT, 'normal');
          doc.setTextColor(59, 130, 246);
          const truncated = fileName.length > 70 ? fileName.slice(0, 67) + '…' : fileName;
          doc.textWithLink(truncated, margin + 14, y, { url: m.downloadUrl });

          y += 4.5;
        }

        // Subtle hint about link expiry
        if (y + 6 > pageHeight - 20) {
          doc.addPage();
          y = margin;
        }
        doc.setFontSize(6.5);
        doc.setFont(PDF_FONT, 'normal');
        doc.setTextColor(150, 150, 150);
        doc.text(
          'Attachment links expire after a short period; regenerate the report if a link no longer works.',
          margin,
          y + 2
        );
        y += 6;
      }

      y += 4;
    }
  }

  // ── Footer on every page ─────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerY = pageHeight - 10;

    // Subtle line above footer
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.15);
    doc.line(margin, footerY - 2, pageWidth - margin, footerY - 2);

    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.setFont(PDF_FONT, 'normal');
    doc.text(
      `Byte Quality Complaints`,
      margin,
      footerY
    );
    doc.text(
      `${complaint.referenceNumber}  •  Page ${i} of ${pageCount}`,
      pageWidth - margin,
      footerY,
      { align: 'right' }
    );

    // Link (rendered as blue text)
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(6);
    const linkUrl = 'https://quality-complaints.mapp.superapp.yum.com';
    const linkText = 'quality-complaints.mapp.superapp.yum.com';
    doc.textWithLink(linkText, margin, footerY + 3, { url: linkUrl });
  }

  const blob = doc.output('blob');
  return { blob, filename: `${complaint.referenceNumber}.pdf` };
}
